import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { ActionSpec, ActionSpecStatus } from "@impero-com/ui";
import { useNavigate } from "react-router-dom";

import shareIcon from "img/share-icon.svg";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { WebRoute } from "../../../routes";

export const USERS_TAB = "USERS";
export const USER_GROUPS_TAB = "USER_GROUPS";

type Tab = typeof USERS_TAB | typeof USER_GROUPS_TAB;

interface InactiveShareableLink {
    state: "inactive";
    reason: string;
}
interface ActiveShareableLink {
    state: "active";
    onCreate: (() => void) | undefined;
}

type ShareableLinkProps = InactiveShareableLink | ActiveShareableLink;

interface UserModuleListApplicationBarProps {
    currentTabId: Tab;
    actions: ActionSpec[];
    children: React.ReactNode;
    shareableLink: ShareableLinkProps;
}

function UserModuleListApplicationBar(
    props: UserModuleListApplicationBarProps
) {
    const { currentTabId, children, actions, shareableLink } = props;
    const navigate = useNavigate();
    let shareableLinkStatus: ActionSpecStatus;
    let onCreateShareableLink: () => void;
    switch (shareableLink.state) {
        case "inactive":
            shareableLinkStatus = {
                type: "DISABLED",
                reason: shareableLink.reason,
            };
            onCreateShareableLink = () => {};
            break;
        case "active":
            shareableLinkStatus = { type: "ENABLED" };
            onCreateShareableLink = shareableLink.onCreate ?? (() => {});
            break;
        default:
            assertNever(shareableLink);
    }

    const actionsWithShareableLink: ActionSpec[] = [
        {
            id: "shareable-link-create-new",
            label: t("ShareableLink_CreateNew"),
            icon: { svg: shareIcon },
            status: shareableLinkStatus,
            onClick: onCreateShareableLink,
        },
        ...actions,
    ];

    return (
        <OrgApplicationBar
            title={t("UserManagement_ModuleTitle")}
            actions={{
                id: "userlist-actions",
                actions: actionsWithShareableLink,
            }}
            navigation={{
                type: "Tabs",
                currentTabId,
                tabSpecs: [
                    {
                        id: USERS_TAB,
                        label: t("User_Users"),
                        onClick: () => {
                            navigate(WebRoute.USERS);
                        },
                    },
                    {
                        id: USER_GROUPS_TAB,
                        label: t("User_UserGroups"),
                        onClick: () => {
                            navigate(WebRoute.USER_GROUPS);
                        },
                    },
                ],
            }}
        >
            {children}
        </OrgApplicationBar>
    );
}

export { UserModuleListApplicationBar };
export type { InactiveShareableLink, ActiveShareableLink, ShareableLinkProps };
